/**
 * ::content widgets:
 * - PrettyContentDefineWidget: L0 pretty widget for content definition header
 * - ContentEndWidget: L0 pretty widget for content definition close
 * - ContentButtonGroupWidget: Run button for content blocks
 */

import { WidgetType } from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";
import {
    attachSyntaxEditInteractions,
    createClearExecutionButton,
    createHoverDetail,
    createInlineRenderToggleButton,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTemplateInputsButton,
    createTextSpan,
    createWatchIndicator,
    shieldGroupEvents,
} from "../shared/widgets";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── PrettyContentDefineWidget — L0 pretty widget for ::content header ── */

export class PrettyContentDefineWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly isTemplate: boolean,
        readonly summary: string,
        readonly isWatched: boolean = false,
    ) {
        super();
    }
    eq(other: PrettyContentDefineWidget) {
        return (
            other.defId === this.defId &&
            other.isTemplate === this.isTemplate &&
            other.summary === this.summary &&
            other.isWatched === this.isWatched
        );
    }
    toDOM(): HTMLElement {
        const border = createSyntaxBadge("C", this.isTemplate ? { sub: "T", template: true } : undefined);
        border.classList.add("cm-magic-content-pretty-border");

        const kindLabel = this.isTemplate
            ? tr("syntax.widget.content.kind.template")
            : tr("syntax.widget.content.kind.content");
        const normalizedSummary = normalizeContentSummary(this.summary, this.isTemplate);

        const wrapper = createPrettySyntaxShell({
            wrapperClass: "cm-magic-content-pretty",
            badge: border,
            primary: [
                createTextSpan("cm-magic-content-pretty-id", `❰ ${this.defId} ❱`),
                this.isWatched ? createWatchIndicator(this.defId) : null,
            ].filter((item): item is HTMLElement => !!item),
            detail: this.summary
                ? createHoverDetail("cm-magic-content-pretty-detail", [
                    createTextSpan("cm-magic-hover-kind cm-magic-content-pretty-kind", kindLabel),
                    normalizedSummary
                        ? createTextSpan(
                            "cm-magic-hover-summary cm-magic-content-pretty-summary",
                            normalizedSummary,
                        )
                        : null,
                ])
                : createHoverDetail("cm-magic-content-pretty-detail", [
                    createTextSpan("cm-magic-hover-kind cm-magic-content-pretty-kind", kindLabel),
                ]),
        });
        attachSyntaxEditInteractions(wrapper);
        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

function normalizeContentSummary(summary: string, isTemplate: boolean): string {
    const label = isTemplate
        ? tr("syntax.summary.template")
        : tr("syntax.summary.contentBlock");
    const separator = tr("common.listSeparator");
    const legacyTemplateLabel = String.fromCharCode(0x6a21, 0x677f);
    const legacyContentBlockLabel = String.fromCharCode(0x5185, 0x5bb9, 0x5757);
    const legacyContentLabel = String.fromCharCode(0x5185, 0x5bb9);
    const stripPrefixes = isTemplate
        ? [`${label}${separator}`, `${legacyTemplateLabel}\uff0c`]
        : [`${label}${separator}`, `${legacyContentBlockLabel}\uff0c`, `${legacyContentLabel}\uff0c`];
    const exactLabels = isTemplate
        ? [label, legacyTemplateLabel]
        : [label, legacyContentBlockLabel, legacyContentLabel];

    for (const prefix of stripPrefixes) {
        if (summary.startsWith(prefix)) {
            return summary.slice(prefix.length).trim();
        }
    }
    if (exactLabels.includes(summary.trim())) return "";

    return summary;
}

/* ── ContentEndWidget — L0 pretty widget for content definition close :: ── */

export class ContentEndWidget extends WidgetType {
    eq(_other: ContentEndWidget) {
        return true;
    }
    toDOM(): HTMLElement {
        const wrapper = document.createElement("span");
        wrapper.className = "cm-magic-content-end";
        // wrapper.textContent = "╚ end"; // OLD: shape icon

        /* OLD: manual badge span
        const badge = document.createElement("span");
        badge.className = "cm-magic-content-pretty-border cm-magic-syntax-badge";
        badge.textContent = "C";
        */
        const badge = createSyntaxBadge("C");
        badge.classList.add("cm-magic-content-pretty-border");
        const endLabel = document.createElement("span");
        endLabel.textContent = " end";
        wrapper.appendChild(badge);
        wrapper.appendChild(endLabel);
        attachSyntaxEditInteractions(wrapper);
        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

/* ── ContentButtonGroupWidget — Run button for content blocks ── */

export class ContentButtonGroupWidget extends WidgetType {
    constructor(
        readonly defId: string,
        readonly status: ExecutionStatus,
        readonly canRun: boolean = true,
        readonly canInlineRender: boolean = true,
        readonly inlineRendered: boolean = false,
        readonly isTemplate: boolean = false,
    ) {
        super();
    }
    eq(other: ContentButtonGroupWidget) {
        return (
            other.defId === this.defId &&
            other.status === this.status &&
            other.canRun === this.canRun &&
            other.canInlineRender === this.canInlineRender &&
            other.inlineRendered === this.inlineRendered &&
            other.isTemplate === this.isTemplate
        );
    }
    toDOM(): HTMLElement {
        const group = document.createElement("span");
        group.className = "cm-magic-btn-group";

        // Prevent mouse events from reaching CodeMirror
        shieldGroupEvents(group);

        const isRunning = this.status === "running";
        const defId = this.defId;

        // Run/Stop button
        const runBtn = document.createElement("span");
        runBtn.className = isRunning
            ? "cm-magic-btn cm-magic-btn-stop"
            : "cm-magic-btn cm-magic-btn-run";
        runBtn.textContent = isRunning ? "■" : "▶";
        runBtn.title = isRunning
            ? tr("syntax.widget.content.stop", { id: this.defId })
            : tr("syntax.widget.content.run", { id: this.defId });
        runBtn.setAttribute(
            "aria-label",
            runBtn.title,
        );
        runBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isRunning) {
                window.dispatchEvent(
                    new CustomEvent("magic-md:cancel-definition", {
                        detail: { definitionId: defId },
                    }),
                );
            } else {
                window.dispatchEvent(
                    new CustomEvent("magic-md:run-content", {
                        detail: { definitionId: defId },
                    }),
                );
            }
        });

        if (this.canRun && !this.isTemplate) {
            group.appendChild(runBtn);
        }

        if (this.isTemplate) {
            group.appendChild(
                createTemplateInputsButton(
                    "content",
                    defId,
                    tr("syntax.widget.content.editInputs", { id: this.defId }),
                ),
            );
        }

        // Inline render toggle
        if (this.canInlineRender && !this.isTemplate) {
            group.appendChild(
                createInlineRenderToggleButton(
                    defId,
                    this.inlineRendered,
                    this.inlineRendered
                        ? tr("syntax.widget.content.hideInlineRender", { id: this.defId })
                        : tr("syntax.widget.content.showInlineRender", { id: this.defId }),
                ),
            );
        }

        if (this.status === "error") {
            group.appendChild(
                createClearExecutionButton(
                    defId,
                    tr("syntax.widget.content.clearError", { id: this.defId }),
                    tr("syntax.widget.content.clearError", { id: this.defId }),
                ),
            );
        }

        return group;
    }
    ignoreEvent(): boolean {
        return true;
    }
}
